import {
	type ComponentType,
	FunctionComponent,
	type PropsWithChildren,
	useEffect,
	useRef,
	ElementType,
} from 'react';

import {
	type IntersectionOptions,
	useInView,
	// @ts-ignore
} from 'react-intersection-observer';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useParentScrollTracking } from '@/utils/scroll-tracking/parent-scroll-tracker.tsx';

import { useAnalyticsClient } from '../../analytics/wac-analytics/analytics-client';

type ScrollTrackingResponse = {
	ref: (node?: Element | null) => void;
	inView: boolean;
	entry?: IntersectionObserverEntry;
};

type AdditionalAttributes = {
	compositionId?: string;
};

type ScrollTrackingProps = {
	/**
	 * @deprecated this prop is deprecated because it is not supported by
	 * manually defining the value. It is only used for automatically
	 * defining the value by the parent scroll tracking container.
	 *
	 * Example of supported usage:
	 * <ParentScrollTracking>
	 *     <ScrollTracking section="section-name">
	 *         Content
	 *     </ScrollTracking>
	 * </ParentScrollTracking>
	 *
	 * Example of unsupported usage:
	 * <ParentScrollTracking>
	 *     <div>
	 *          <ScrollTracking section="section-name">
	 *              Content
	 *          </ScrollTracking>
	 *     </div>
	 * </ParentScrollTracking>
	 */
	index?: string;
	/**
	 * Options to customize the intersection observer
	 * Default values will always be set to:
	 * - threshold: 0.5
	 * - triggerOnce: true
	 */
	observerOptions?: IntersectionOptions;
	/**
	 * Whether to track dwell time for the component
	 */
	dwellTimeEnabled?: boolean;
	/**
	 * Additional attributes to include in the tracking event
	 */
	additionalAttributes?: AdditionalAttributes;
};

type ScrollTrackingComponentProps = {
	/**
	 * Section identifier used in the tracking attributes
	 */
	section: string;

	/**
	 * The top-level scroll-tracker element. Defaults to "div"
	 */
	as?: ElementType;
} & ScrollTrackingProps;

type AnalyticsPayload = {
	eventType: string;
	action: string;
	actionSubject: string;
	source: string;
	attributes: {
		section: string | undefined;
		scrollPercentage: number;
		pageChildIndex?: string;
		pageId?: string;
	};
};

/**
 * useScrollTracking hook to track scroll events. Used to track when a section is in view.
 * @param section Section identifier used in the tracking attributes
 * @param props ScrollTrackingHookProps if you want to customize the observer options
 *              if you want to use the inView value in the response you should set triggerOnce to false
 */
export const useScrollTracking = (
	section: string,
	props: ScrollTrackingProps = {},
): ScrollTrackingResponse => {
	const { index, observerOptions, dwellTimeEnabled, additionalAttributes } = props;
	const hasFunctionBeenCalled = useRef(false);
	const scrollPercentage = useRef(0);
	const { pageId, scrollTrackingEnabled } = useParentScrollTracking();
	const { ref, inView, entry } = useInView({
		threshold: 0.5,
		triggerOnce: true,
		...observerOptions,
		skip: observerOptions?.skip || !scrollTrackingEnabled,
	});
	const analyticsClient = useAnalyticsClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleScroll = () => {
		const windowHeight = window.innerHeight;
		const fullHeight = document.documentElement.scrollHeight;
		const currentPosition = window.scrollY;

		scrollPercentage.current = Math.round((currentPosition / (fullHeight - windowHeight)) * 100);
	};

	useEffect(() => {
		if (inView && !hasFunctionBeenCalled.current && section) {
			handleScroll();

			const scrollTrackAnalyticsPayload: AnalyticsPayload = {
				eventType: 'ui',
				action: 'scrolledTo',
				actionSubject: 'section',
				source: window.location.pathname,
				attributes: {
					section: section,
					scrollPercentage: scrollPercentage.current,
					pageChildIndex: index,
					pageId,
					...additionalAttributes,
				},
			};

			const dwellTimeTrackAnalyticsPayload: AnalyticsPayload = {
				eventType: 'ui',
				action: 'dwellTimeTracked',
				actionSubject: 'section',
				source: window.location.pathname,
				attributes: {
					section: section,
					scrollPercentage: scrollPercentage.current,
					pageChildIndex: index,
					pageId,
					...additionalAttributes,
				},
			};

			if (analyticsClient) {
				createAnalyticsEvent({
					...scrollTrackAnalyticsPayload,
				}).fire();
				hasFunctionBeenCalled.current = true;
			}
			if (dwellTimeEnabled) {
				const activityEvents = ['mousemove', 'keydown', 'scroll', 'touchstart'];
				let intervalId: NodeJS.Timeout | undefined = undefined;
				let activityTimeout: NodeJS.Timeout | undefined = undefined;
				let active = true;
				// stop tracking after 1 minute of inactivity
				const setActive = () => {
					active = true;
					clearTimeout(activityTimeout);
					activityTimeout = setTimeout(() => {
						active = false;
					}, 60000);
				};
				activityEvents.forEach((evt) => {
					window.addEventListener(evt, setActive);
				});
				// emmit every 5 seconds if the user is active
				intervalId = setInterval(() => {
					if (active) {
						handleScroll();
						createAnalyticsEvent({
							...dwellTimeTrackAnalyticsPayload,
						}).fire();
					} else {
						clearInterval(intervalId);
						clearTimeout(activityTimeout);
						activityEvents.forEach((evt) => {
							window.removeEventListener(evt, setActive);
						});
					}
				}, 5000);
			}
		}
	}, [
		additionalAttributes,
		analyticsClient,
		createAnalyticsEvent,
		dwellTimeEnabled,
		hasFunctionBeenCalled,
		inView,
		index,
		pageId,
		section,
	]);
	return { ref, inView, entry };
};

/**
 * ScrollTracking component to track scroll events.
 * @param section The section name to track
 * @param index The index of the section (used internally)
 * @param observerOptions options for the intersection observer
 * @param as The root-element type. Defaults to "div"
 * @param children
 */
export const ScrollTracking = ({
	section,
	index,
	observerOptions,
	children,
	as: RootElement = 'div',
}: PropsWithChildren<ScrollTrackingComponentProps>) => {
	const { ref } = useScrollTracking(section, { index, observerOptions });

	return <RootElement ref={ref}>{children}</RootElement>;
};

/**
 * HOC to wrap a component with ScrollTracking
 * @param section The section name to track
 * @param InnerComponent The component to wrap
 * @param options options for the intersection observer
 */
export function withScrollTracking<TProps>(
	section: string,
	InnerComponent: ComponentType<TProps>,
	options?: ScrollTrackingProps,
): FunctionComponent<TProps> {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return (props: any) => {
		return (
			<ScrollTracking section={section} {...options}>
				<InnerComponent {...props} />
			</ScrollTracking>
		);
	};
}
